/* eslint-disable @typescript-eslint/restrict-template-expressions */
import React from 'react';
import {
  Box,
  Card, CardContent, CardHeader,
  Divider,
  Grid,
  Typography,
} from '@material-ui/core';
import { useForm } from 'react-hook-form';
import { Skeleton } from '@material-ui/lab';

import ChipSelect from '../ChipSelect';
import ReactHookFormAutoComplete from '../global/forms/ReactHookFormAutoComplete';
import ReactHookFormTextField from '../global/forms/ReactHookFormTextField';
import type { ILabelValuePair, IRepairItemProps } from './MarkingInterfaces';
import getBrands from '../../lib/SWRfetchers/getBrands';
import getManufacturers from '../../lib/SWRfetchers/getManufacturers';
import getSettingTypes from '../../lib/SWRfetchers/getSettingTypes';
import getMetalTypes from '../../lib/SWRfetchers/getMetalTypes';
import getWritingInstrumentTypes from '../../lib/SWRfetchers/getWritingInstrumentTypes';
import getRepairItemTypes from '../../lib/SWRfetchers/getRepairItemTypes';

const RepairItem: React.FC<IRepairItemProps> = ({
  index, length, repairItem, reactHookControl, reactHookRegister, reactHookSetValue, markingBounds,
}) => {
  const {
    trigger,
  } = useForm({
    mode: 'all',
    criteriaMode: 'all',
  });

  const [repairItemType, setRepairItemType] = React.useState<ILabelValuePair>(
    repairItem?.repairItemType?.label === 'Watch'
      ? repairItem?.repairItemType : repairItem?.jewelryType,
  );

  const [ringSizes, setRingSizes] = React.useState<ILabelValuePair[]>([]);

  const { brands } = getBrands();
  const { manufacturers } = getManufacturers();
  const { settingTypes } = getSettingTypes();
  const { metalTypes } = getMetalTypes();
  const { writingInstrumentTypes } = getWritingInstrumentTypes();
  const { repairItemTypes } = getRepairItemTypes();

  const isJewelry = (repairItemType?.label === 'Ring'
    || repairItemType?.label === 'Earrings'
    || repairItemType?.label === 'Necklace'
    || repairItemType?.label === 'Bracelet');

  reactHookRegister(`${index}.index`);
  reactHookSetValue(`${index}.index`, index);
  reactHookRegister(`${index}.repairItemId`);
  reactHookSetValue(`${index}.repairItemId`, repairItem?.repairItemId || '00000000-0000-0000-0000-000000000000');
  reactHookRegister(`${index}.repairItemType`);
  reactHookSetValue(`${index}.repairItemType`, repairItemType || { value: '1', label: 'Ring' });
  reactHookRegister(`${index}.itemNumber`);
  reactHookSetValue(`${index}.itemNumber`, String(repairItem?.itemNumber || (index + 1)).padStart(2, '0'));

  function GetRange(start: number, stop: number, step: number) {
    return Array.from(
      { length: (stop - start) / step + 1 },
      (_, i) => start + (i * step),
    );
  }

  React.useEffect(() => {
    function GetRingSizes() {
      const rs = GetRange(2, 20, 0.25)
        .map<ILabelValuePair>(
        (e) => ({ label: e.toString(), value: e.toString() }),
      );
      setRingSizes(rs);
    }
    GetRingSizes();
    if (repairItem?.repairItemId) {
      reactHookSetValue(`${index}.repairItemId`, repairItem.repairItemId);
    }
  }, [repairItem?.repairItemId, reactHookSetValue, index]);

  function IndividualRepairItemHeader() {
    return (
      <Grid container>
        <Grid item xs={6}>
          <CardHeader title="Individual Repair Item" />
        </Grid>
        <Grid item xs={6}>
          <Box p={2} alignItems="center">
            <Typography align="right" color="textSecondary">{`${index + 1} OF ${length} REPAIR ITEMS`}</Typography>
          </Box>
        </Grid>
      </Grid>
    );
  }

  const handleChipSelect = (selectedType: ILabelValuePair) => {
    setRepairItemType(selectedType);
    reactHookSetValue(`${index}.repairItemType`, selectedType);
  };

  /* eslint-disable*/
  const checkMarkingBounds = (jewelryType: string, fieldName: string, currentValue: number) => {
    if (markingBounds) {
      if (jewelryType === 'Ring') {
        if (fieldName.includes('Size')) {
          return (currentValue < markingBounds.Ring?.Size.Min || currentValue > markingBounds.Ring?.Size.Max);
        } else if (fieldName.includes('Width')) {
          return (currentValue < markingBounds.Ring?.Width.Min || currentValue > markingBounds.Ring?.Width.Max);
        } else if (fieldName.includes('Thickness')) {
          return (currentValue < markingBounds.Ring?.Thickness.Min || currentValue > markingBounds.Ring?.Thickness.Max);
        } else if (fieldName.includes('Diameter')) {
          return (currentValue < markingBounds.Ring?.Diameter.Min || currentValue > markingBounds.Ring?.Diameter.Max);
        }
      } else if (jewelryType === 'Necklace') {
        if (fieldName.includes('Length')) {
          return (currentValue < markingBounds.Necklace?.Length.Min || currentValue > markingBounds.Necklace?.Length.Max);
        }
      } else if (jewelryType === 'Bracelet') {
        if (fieldName.includes('Length')) {
          return (currentValue < markingBounds.Bracelet?.Length.Min || currentValue > markingBounds.Bracelet?.Length.Max);
        }
      }
    }
    return false;
  };

  const checkIsRequiredFromMarkingBounds = (jewelryType: string, fieldName: string): boolean => {
    if (markingBounds) {
      switch (jewelryType) {
        case 'Accessory':
        case 'Bracelet':
        case 'Bulk':
        case 'Earrings':
        case 'Eyewear':
        case 'LeatherGood':
        case 'Necklace':
        case 'Ring':
        case 'Watch':
        case 'WritingInstrument':
          return (markingBounds as any)[jewelryType][fieldName]?.Required;
        default:
          return false;
      }
    }
    return false;
  };
  /* eslint-enable */

  function RepairItemTypes() {
    return (
      <>
        <Grid item xs={12}>
          <Typography variant="h6" display="inline">
            {'Repair Type'}
          </Typography>
          <Typography variant="body1" color="textSecondary" display="inline">
            {
              !(repairItem?.repairItemId && repairItem?.repairItemId !== '00000000-0000-0000-0000-000000000000')
              && (
                <Box component="span" ml={1}>
                  {'Choose one'}
                </Box>
              )
            }
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid
            container
            justifyContent={repairItem?.repairItemId && repairItem?.repairItemId !== '00000000-0000-0000-0000-000000000000' ? 'flex-start' : 'space-evenly'}
          >
            {
              repairItemTypes
                ? repairItemTypes
                  .filter((item) => (
                    (repairItem?.repairItemId && repairItem?.repairItemId !== '00000000-0000-0000-0000-000000000000')
                      ? (item.label === repairItemType?.label)
                      : true
                  ))
                  .map((t) => (
                    <Grid item>
                      <ChipSelect
                        key={t.label}
                        label={t?.label?.match(/[A-Z][a-z]+|[0-9]+/g)?.join(' ') || t.label}
                        selected={(repairItemType?.label || 'ring') === t.label}
                        handleClick={() => handleChipSelect(t)}
                      />
                    </Grid>
                  ))
                : (
                  <Grid item>
                    <Skeleton variant="circle">
                      <ChipSelect
                        label="Item"
                        selected={false}
                        handleClick={() => { }}
                      />
                    </Skeleton>
                  </Grid>
                )
            }
          </Grid>
        </Grid>
      </>
    );
  }

  return (
    <>
      <Card>
        <IndividualRepairItemHeader />

        <Divider variant="fullWidth" />

        <CardContent>
          <Grid container spacing={2}>

            {
              length > 1
              && (
                <Grid item xs={6}>
                  <ReactHookFormTextField
                    label="Repair Item Label"
                    name={`${index}.label`}
                    required={length > 1}
                    control={reactHookControl}
                    defaultValue={repairItem?.label}
                  />
                </Grid>
              )
            }
            <RepairItemTypes />

            <Grid item xs={12}>
              <Typography variant="h6" display="inline">
                {`${(repairItemType?.label?.match(/[A-Z][a-z]+|[0-9]+/g)?.join(' ') || repairItemType?.label) || 'Item'} Measurements`}
              </Typography>
            </Grid>

            {(repairItemType?.label === 'Necklace' || repairItemType?.label === 'Bracelet')
              && (
                <Grid item lg={4} md={6} xs={12}>
                  <ReactHookFormTextField
                    label="Length (In)"
                    name={`${index}.length`}
                    type="number"
                    required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'Length')}
                    control={reactHookControl}
                    defaultValue={repairItem?.length}
                    checkMarkingBounds={checkMarkingBounds}
                    jewelryType={repairItemType?.label}
                  />
                </Grid>
              )}

            {repairItemType?.label === 'Ring'
              && (
                <>
                  <Grid item lg={4} md={6} xs={12}>
                    <ReactHookFormTextField
                      label="Width (mm)"
                      name={`${index}.width`}
                      type="number"
                      required={markingBounds?.Ring.Width.Required}
                      control={reactHookControl}
                      defaultValue={repairItem?.width}
                      checkMarkingBounds={checkMarkingBounds}
                      jewelryType={repairItemType?.label}
                    />
                  </Grid>

                  <Grid item lg={4} md={6} xs={12}>
                    <ReactHookFormTextField
                      label="Thickness (mm)"
                      name={`${index}.thickness`}
                      type="number"
                      required={markingBounds?.Ring.Thickness.Required}
                      control={reactHookControl}
                      defaultValue={repairItem?.thickness}
                      checkMarkingBounds={checkMarkingBounds}
                      jewelryType={repairItemType?.label}
                    />
                  </Grid>

                  <Grid item lg={4} md={6} xs={12}>
                    <ReactHookFormTextField
                      name={`${index}.stoneDiameter`}
                      required={markingBounds?.Ring.Diameter.Required}
                      label="Stone Diameter (mm)"
                      type="number"
                      control={reactHookControl}
                      defaultValue={repairItem?.stoneDiameter}
                      checkMarkingBounds={checkMarkingBounds}
                      jewelryType={repairItemType?.label}
                    />
                  </Grid>

                  <Grid item lg={4} md={6} xs={12}>
                    <ReactHookFormAutoComplete
                      name={`${index}.size`}
                      id="ring-size-select"
                      required={markingBounds?.Ring.Size.Required}
                      label="Ring Size"
                      options={ringSizes}
                      control={reactHookControl}
                      defaultValue={repairItem?.size}
                      onBlurFunction={async () => { await trigger(`${index}.size`); }}
                      checkMarkingBounds={checkMarkingBounds}
                      jewelryType={repairItemType?.label}
                    />
                  </Grid>
                </>
              )}

            {
              isJewelry
              && (
                <>
                  <Grid item lg={4} md={6} xs={12}>
                    {settingTypes
                      ? (
                        <ReactHookFormAutoComplete
                          id="setting-type-select"
                          name={`${index}.settingType`}
                          required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'SettingType')}
                          label="Setting Type"
                          options={settingTypes}
                          control={reactHookControl}
                          defaultValue={repairItem?.settingType}
                          onBlurFunction={async () => { await trigger(`${index}.settingType`); }}
                        />
                      ) : <Skeleton height="56px" variant="rect" />}
                  </Grid>
                </>
              )
            }

            {/*
            TODO: SM - Commenting this whole section. This can be removed in next PRs.
            {
              !isJewelry
              && (
                <Grid item lg={4} md={6} xs={12}>
                  <ReactHookFormAutoComplete
                    id="stamped-select"
                    name={`${index}.stamped`}
                    required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'Stamped')}
                    label="Stamped"
                    options={[{ label: 'Yes', value: 'true' }, { label: 'No', value: 'false' }]}
                    control={reactHookControl}
                    defaultValue={repairItem?.stamped}
                    onBlurFunction={async () => { await trigger(`${index}.stamped`); }}
                  />
                </Grid>
              )
            } */}

            {repairItemType?.label === 'Watch'
              && (
                <Grid item lg={4} md={6} xs={12}>
                  <ReactHookFormTextField
                    label="Case Number"
                    name={`${index}.caseNumber`}
                    control={reactHookControl}
                    defaultValue={repairItem?.caseNumber}
                    preventSpecialCharacters
                    required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'CaseNumber')}
                  />
                </Grid>
              )}

            {(repairItemType?.label === 'Watch'
              || repairItemType?.label === 'WritingInstrument')
              && (
                <>
                  <Grid item lg={4} md={6} xs={12}>
                    <ReactHookFormTextField
                      label="Model Number"
                      name={`${index}.modelNumber`}
                      required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'ModelNumber')}
                      control={reactHookControl}
                      defaultValue={repairItem?.modelNumber}
                      preventSpecialCharacters
                    />
                  </Grid>

                  <Grid item lg={4} md={6} xs={12}>
                    <ReactHookFormTextField
                      label="Serial Number"
                      name={`${index}.serialNumber`}
                      required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'SerialNumber')}
                      control={reactHookControl}
                      defaultValue={repairItem?.serialNumber}
                      preventSpecialCharacters
                    />
                  </Grid>
                </>
              )}

            {repairItemType?.label === 'WritingInstrument'
              && (
                <Grid item lg={4} md={6} xs={12}>
                  {writingInstrumentTypes
                    ? (
                      <ReactHookFormAutoComplete
                        id="writing-instrument-type-select"
                        name={`${index}.writingInstrumentType`}
                        required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'WritingInstrumentType')}
                        label="Writing Instrument Type"
                        options={writingInstrumentTypes}
                        control={reactHookControl}
                        defaultValue={repairItem?.writingInstrumentType}
                        onBlurFunction={async () => { await trigger(`${index}.writingInstrumentType`); }}
                      />
                    ) : <Skeleton height="56px" variant="rect" />}
                </Grid>
              )}

            {repairItemType?.label === 'Watch'
              && (
                <Grid item lg={4} md={6} xs={12}>
                  {brands
                    ? (
                      <ReactHookFormAutoComplete
                        id="brand-select"
                        name={`${index}.brand`}
                        required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'Brand')}
                        label="Brand"
                        options={brands}
                        control={reactHookControl}
                        defaultValue={repairItem?.brand}
                        onBlurFunction={async () => { await trigger(`${index}.brand`); }}
                      />
                    ) : <Skeleton height="56px" variant="rect" />}
                </Grid>
              )}

            {(isJewelry
              && (
                <Grid item lg={4} md={6} xs={12}>
                  {(
                    metalTypes
                  )
                    ? (
                      <ReactHookFormAutoComplete
                        id="metal-type-select"
                        name={`${index}.metalType`}
                        required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'MetalType')}
                        label="Metal Type"
                        options={metalTypes}
                        control={reactHookControl}
                        defaultValue={repairItem?.metalType}
                        onBlurFunction={async () => { await trigger(`${index}.metalType`); }}
                      />
                    ) : <Skeleton height="56px" variant="rect" />}
                </Grid>
              )
            )}

            {
              repairItemType?.label !== 'Watch'
              && (
                <Grid item lg={4} md={6} xs={12}>
                  {manufacturers
                  && (
                    <ReactHookFormAutoComplete
                      id="manufacturer-select"
                      name={`${index}.manufacturer`}
                      required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'Manufacturer')}
                      label="Manufacturer"
                      options={manufacturers}
                      control={reactHookControl}
                      defaultValue={repairItem?.manufacturer}
                      onBlurFunction={async () => { await trigger(`${index}.manufacturer`); }}
                    />
                  )}
                </Grid>
              )
            }
            {
              (repairItemType?.label !== 'Bulk'
              && repairItemType?.label !== 'WritingInstrument' && repairItemType?.label !== 'Accessory'
              && repairItemType?.label !== 'LeatherGood')
                && (
                  <>
                    <Grid item lg={4} md={6} xs={12}>
                      <ReactHookFormTextField
                        label="SKU"
                        name={`${index}.sku`}
                        control={reactHookControl}
                        defaultValue={repairItem?.sku}
                        preventSpecialCharacters
                        required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'SKU')}
                      />
                    </Grid>
                    <Grid item lg={4} md={6} xs={12}>
                      <ReactHookFormTextField
                        label="Tag Number"
                        name={`${index}.tagNumber`}
                        control={reactHookControl}
                        defaultValue={repairItem?.tagNumber}
                        preventSpecialCharacters
                        required={checkIsRequiredFromMarkingBounds(repairItemType?.label, 'TagNumber')}
                      />
                    </Grid>
                  </>
                )
            }
          </Grid>
        </CardContent>
      </Card>
    </>
  );
};

export default RepairItem;
